// Saved, named column mappings for Import Leads' Map Fields step (#19586;
// owner-approved mockup claude.ai/artifact/74b1JWfA8YywHrAH9LHJfY). The pure
// half: applying a saved mapping to a file's columns, the columns a save
// sends, and the words the notice and Manage Mappings show. The Stimulus
// side lives in controllers/import_wizard_controller.js; which mapping is
// applied automatically is the server's LeadImports::SavedMappingMatcher.

// = LeadImportMapping.normalize_column: case, spacing and order never matter.
export function normalizeColumn (name) {
  return String(name ?? "").toLowerCase().replace(/\s+/g, "")
}

// Apply `savedColumns` ({ column key → value }) to a file whose columns are
// `keys` (shown as `names`). A column the mapping knows takes its saved
// value; one it does not know stays not mapped (""), so Ignore All
// Unmapped reaches it. Returns the new mapping plus what the notice reports:
// the file's columns the mapping does not know (newColumns), the saved
// columns this file lacks (skipped), and how many of the file's columns it
// filled in (known of total).
export function applySavedColumns (keys, names, savedColumns = {}) {
  const saved = new Map()
  Object.entries(savedColumns).forEach(([column, value]) => {
    const key = normalizeColumn(column)
    if (key && !saved.has(key)) saved.set(key, { column, value: String(value ?? "") })
  })
  const mapping = {}
  const newColumns = []
  const seen = new Set()
  keys.forEach((key, index) => {
    const normalized = normalizeColumn(key)
    const entry = saved.get(normalized)
    mapping[key] = entry ? entry.value : ""
    if (entry) seen.add(normalized)
    else newColumns.push(names[index] ?? key)
  })
  const skipped = [...saved.entries()].filter(([key]) => !seen.has(key)).map(([, entry]) => entry.column)
  return { mapping, newColumns, skipped, known: keys.length - newColumns.length, total: keys.length }
}

// What Save Mapping stores: every column of the file, including the ones
// left not mapped (""), so the same file next week counts as the same layout.
export function columnsForSave (keys, mapping = {}) {
  return Object.fromEntries(keys.map((key) => [key, mapping[key] ?? ""]))
}

function plural (count, one, many) {
  return `${count} ${count === 1 ? one : many}`
}

// The notice's sentence, by how the mapping was chosen.
export function appliedSentence ({ how, known, total, ghlAccount }) {
  if (how === "ghl") {
    const account = ghlAccount ? ` (${ghlAccount})` : ""
    return `This import comes from the same GHL account as your saved mapping${account}, so ${known} of its ${total} columns were filled in for you.`
  }
  if (how === "layout") return `This file’s columns match your saved mapping, so ${known} of its ${total} columns were filled in for you.`
  return `${known} of this file’s ${total} columns were filled in from your saved mapping.`
}

// The notice's bullets: [lead text, names] pairs. The new-column names are
// listed only while there are four or fewer of them.
export function appliedBullets ({ newColumns = [], skipped = [] }) {
  const bullets = []
  if (newColumns.length > 0) {
    const verb = newColumns.length === 1 ? "is" : "are"
    const lead = `${plural(newColumns.length, "column isn’t", "columns aren’t")} in the saved mapping and ${verb} not mapped yet`
    bullets.push(newColumns.length <= 4 ? [`${lead}: `, newColumns] : [`${lead}.`, []])
  }
  if (skipped.length > 0) {
    const verb = skipped.length === 1 ? "was" : "were"
    bullets.push([`${plural(skipped.length, "saved column isn’t", "saved columns aren’t")} in this file and ${verb} skipped: `, skipped])
  }
  return bullets
}

// Manage Mappings' "Applied Automatically When".
export function appliedWhen (mapping) {
  if (!mapping?.ghl) return "Same columns"
  return mapping.ghl_account ? `Same GHL account (${mapping.ghl_account}) or same columns` : "Same GHL account or same columns"
}

// Last Used as a date in the agent's zone (the :mdy_slash shape), or ""
// for a mapping never used — the cell then shows the shared em dash.
export function formatLastUsed (iso, timeZone) {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  const options = { month: "numeric", day: "numeric", year: "numeric" }
  if (timeZone) options.timeZone = timeZone
  return new Intl.DateTimeFormat("en-US", options).format(date)
}
